/**
 * callLogsUrl — the Call logs view + filters live in the URL.
 *
 * The sidenav's `call-logs` level renders the view switcher and the outcome /
 * sentiment filters, and the page body renders the matching list. Both read
 * the same query string, so a sidebar click is just a navigation:
 *
 *   /b/{id}/call-logs                         Calls, no filters
 *   /b/{id}/call-logs?outcome=resolved        Calls, resolved only
 *   /b/{id}/call-logs?view=analytics          Analytics
 *
 * Defaults (`view=calls`, `outcome=all`, `sentiment=all`) are omitted from the
 * URL so the clean path is the canonical unfiltered one.
 */

export type CallLogsView = 'calls' | 'analytics' | 'users';
export type OutcomeFilter = 'all' | 'resolved' | 'unresolved';
export type SentimentFilter = 'all' | 'positive' | 'neutral' | 'negative';

export interface CallLogsParams {
  view: CallLogsView;
  outcome: OutcomeFilter;
  sentiment: SentimentFilter;
}

export const DEFAULT_CALL_LOGS_PARAMS: CallLogsParams = {
  view: 'calls',
  outcome: 'all',
  sentiment: 'all',
};

const VIEWS: CallLogsView[] = ['calls', 'analytics', 'users'];
const OUTCOMES: OutcomeFilter[] = ['all', 'resolved', 'unresolved'];
const SENTIMENTS: SentimentFilter[] = ['all', 'positive', 'neutral', 'negative'];

function pick<T extends string>(allowed: T[], raw: string | null, fallback: T): T {
  const v = (raw || '').toLowerCase() as T;
  return allowed.includes(v) ? v : fallback;
}

/** Parse `?view=…&outcome=…&sentiment=…`; unknown values fall back to defaults. */
export function parseCallLogsParams(search: string): CallLogsParams {
  const sp = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return {
    view: pick(VIEWS, sp.get('view'), 'calls'),
    outcome: pick(OUTCOMES, sp.get('outcome'), 'all'),
    sentiment: pick(SENTIMENTS, sp.get('sentiment'), 'all'),
  };
}

/** Build the call-logs URL for `params` (missing keys fall back to defaults). */
export function callLogsHref(botId: string, params: Partial<CallLogsParams>): string {
  const merged = { ...DEFAULT_CALL_LOGS_PARAMS, ...params };
  const sp = new URLSearchParams();
  if (merged.view !== 'calls') sp.set('view', merged.view);
  if (merged.outcome !== 'all') sp.set('outcome', merged.outcome);
  if (merged.sentiment !== 'all') sp.set('sentiment', merged.sentiment);
  const q = sp.toString();
  return `/b/${botId}/call-logs${q ? `?${q}` : ''}`;
}

export function hasActiveCallLogsFilters(params: CallLogsParams): boolean {
  return params.outcome !== 'all' || params.sentiment !== 'all';
}
